/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Apr 17 2023
 **/

import { useCallback, useContext } from "react";
import { FoldingBStateContext, FoldingBStateContextType } from "./FoldingBContainer";

export default function useFoldingB() {
	const context = useContext<FoldingBStateContextType | null>(FoldingBStateContext);

	if (!context) {
		throw new Error("useFoldingB must be used within a FoldingBProvider");
	}

	const { state, dispatch } = context;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleRegister = (id: string, defaultValue: { [x: string]: any }) => {
		dispatch({
			type: "register",
			payload: {
				id,
				...defaultValue
			}
		});
	};

	const handleUnregister = (id: string) => {
		dispatch({
			type: "unregister",
			payload: {
				id
			}
		});
	};

	const handleShow = (id: string, meta: "click" | "system") => {
		dispatch({
			type: "show",
			payload: {
				id,
				meta: meta
			}
		});
	};

	const handleHide = (id: string, meta: "click" | "system") => {
		dispatch({
			type: "hide",
			payload: {
				id,
				meta
			}
		});
	};

	const handleShowAll = () => {
		dispatch({
			type: "showAll",
			payload: null
		});
	};

	const handleHideAll = () => {
		dispatch({
			type: "hideAll",
			payload: null
		});
	};

	const handleToggle = useCallback(
		(id: string) => {
			if (state[id].isOpen) {
				handleHide(id, "click");
			} else {
				handleShow(id, "click");
			}
		},
		[state]
	);

	const getPrevID = useCallback(
		(id: string) => {
			const keys = Object.keys(state);
			const index = keys.findIndex((key) => key === id);
			if (index === 0) {
				return null;
			}
			return keys[index - 1];
		},
		[state]
	);

	return {
		state,
		handlers: {
			handleRegister,
			handleUnregister,
			handleShow,
			handleHide,
			handleShowAll,
			handleHideAll,
			handleToggle,
			getPrevID
		}
	};
}
